import { useCallback, useEffect, useState } from 'react';
import { useReloadOnChange } from '../lib/changes';
import { api, quarterLabel, type EditOutcome, type Site, type SiteQuarter } from '../lib/api';
import { dayLabel, quarterDates, shiftQuarter } from '../lib/dates';
import { navigate } from '../lib/router';
import { ScheduleGrid, printPages } from './ScheduleGrid';

/**
 * One hospital, one month (D31, turned on its side by D64 — John's ask): days run across the
 * top and positions down the side, cells are names. That is how a rota is read at a hospital:
 * a position is a line you follow left to right, and who is on it tomorrow is the next cell
 * along rather than a screen further down.
 *
 * Printable as it stands (File → Print) — and now landscape, because a month of days is wide.
 */
export function Hospital({ siteId, quarter, sites, editedBy }: { siteId: string; quarter: string; sites: Site[]; editedBy: string }) {
  // The month grid is tinted with this hospital's own colour (D63) — `sitetint` opts in, and
  // `site-<id>` carries the palette — so switching hospitals is visible at a glance rather
  // than only in the picker.
  const [data, setData] = useState<SiteQuarter | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback((blank: boolean) => {
    setError(null);
    if (blank) setData(null);
    api.siteQuarter(siteId, quarter).then(setData).catch((e: Error) => setError(e.message));
  }, [siteId, quarter]);
  useEffect(() => load(true), [load]);

  // Shifts can be changed here as well as on the week page (D71): the grid is where John reads
  // the rota, so it is where he notices something is wrong. The grid is refetched rather than
  // patched in place — an edit can move a second shift (a handover) or trip a rule elsewhere.
  const site = sites.find((s) => s.id === siteId);
  // Reloading is not done here: every shift edit announces itself and `useReloadOnChange` below
  // refetches, so a change made on ANY screen lands on this one too — not only the ones made
  // here (D93). Doing both would fetch the same quarter twice for one edit.
  const changed = (o: EditOutcome, what: string) => {
    setToast(`${what}. ${o.introduced.length ? `${o.introduced.length} error(s) overridden.` : 'No new errors.'}`);
  };
  useReloadOnChange(() => load(false));
  const editing = site && editedBy ? { site, editedBy, onChanged: changed } : undefined;

  const go = (id: string, q: string) => navigate(`/admin/hospitals/${id}?quarter=${q}`);

  return (
    <section className="hospital">
      {/* A month of days is wide, so this one page prints landscape. Scoped by being rendered
          from this component: no other screen's print is affected (D64). */}
      <style>{'@media print { @page { size: landscape; } }'}</style>
      <div className="gridtools noprint">
        <label className="field inline">
          <span className="muted">Hospital</span>
          <select value={siteId} onChange={(e) => go(e.target.value, quarter)}>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <div className="quarterpicker">
          <button type="button" onClick={() => go(siteId, shiftQuarter(quarter, -1))} aria-label="Previous quarter">‹</button>
          <h1>{quarterLabel(quarter)} <span className="quarterspan">{quarterDates(quarter)}</span></h1>
          <button type="button" onClick={() => go(siteId, shiftQuarter(quarter, 1))} aria-label="Next quarter">›</button>
        </div>
        <button className="linklike" type="button" onClick={() => window.print()}>print</button>
      </div>
      {toast && <p className="toast noprint" onClick={() => setToast(null)}>{toast}</p>}
      {!editedBy && <p className="muted small noprint">Enter your name at the top of the page to change a shift from here — every edit is recorded with who made it.</p>}
      {error && <p className="error">Couldn't load this month ({error}).</p>}
      {!data && !error && <p className="muted">Loading…</p>}
      {data && (
        <>
          <h1 className="printonly">{data.site.name} — {quarterLabel(quarter)} ({quarterDates(quarter)})</h1>
          {data.slots.length === 0 ? (
            <p className="muted">No published shifts at {data.site.name} in {quarterLabel(quarter)}.</p>
          ) : (
            <>
              {/* On screen: the whole month in one box that scrolls sideways. */}
              <div className={`gridscroll sitetint site-${siteId} noprint`}>
                <ScheduleGrid siteId={siteId} slots={data.slots} days={data.days} editing={editing} />
              </div>
              {/* On paper: the same grid in halves. Thirty-one day columns do not fit across a
                  page even landscape, and a table cannot break across columns — so it is split
                  rather than clipped, which is what printing it used to do silently. */}
              <div className={`printonly sitetint site-${siteId}`}>
                {printPages(data.days).map((part, i) => (
                  <div key={i} className="printhalf">
                    <p className="small muted">{quarterLabel(quarter)} · {dayLabel(part[0].date).replace(/^\w+, /, '')} – {dayLabel(part[part.length - 1].date).replace(/^\w+, /, '')}</p>
                    <ScheduleGrid siteId={siteId} slots={data.slots} days={part} />
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}
